import dayjs from "dayjs";
import { jsPDF } from "jspdf";

import type { AccountMoveLine, AccountMovement } from "~/types/contabilidad";

const LOGO_URL =
  "https://mdrvhekhimhcwydrpueo.supabase.co/storage/v1/object/public/uploads/assets/aliace-logo.jpg";

// Amounts match the on-screen "Líneas del Comprobante" table (2 decimals, es-CL).
const formatAmount = (value: number): string =>
  new Intl.NumberFormat("es-CL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

// Load image and convert to base64 (same pattern as quotationPdf / purchaseOrderPdf).
const loadImage = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/jpeg"));
    };
    img.onerror = reject;
    img.src = url;
  });
};

interface ComprobantePdfData {
  movement: AccountMovement;
  lines: AccountMoveLine[];
}

/**
 * Generates a printable PDF of an accounting voucher (comprobante contable):
 * company letterhead + header info + the full lines table (paginated) + totals.
 * Opens it in a new tab and auto-triggers the browser print dialog so users can
 * print (or save) instead of screenshotting the page.
 */
export async function generateComprobantePdf({
  movement,
  lines,
}: ComprobantePdfData): Promise<void> {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;

  // Try to load logo (best-effort; the PDF still renders without it).
  let logoBase64: string | null = null;
  try {
    logoBase64 = await loadImage(LOGO_URL);
  } catch (e) {
    console.warn("Could not load logo:", e);
  }

  // ==================== HEADER SECTION ====================
  let y = 12;

  if (logoBase64) {
    doc.addImage(logoBase64, "JPEG", margin, y, 40, 20);
  }

  const centerX = pageWidth / 2;
  doc.setTextColor(0);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("ALIMENTOS Y ACEITES SPA", centerX, y + 4, { align: "center" });

  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.text(
    "COMERCIALIZACION Y DISTRIBUCION DE PRODUCTOS ALIMENTICIOS",
    centerX,
    y + 9,
    { align: "center" }
  );
  doc.text("CARRETERA GENERAL SAN MARTIN N° 9340, BOD. 6", centerX, y + 13, {
    align: "center",
  });
  doc.text("QUILICURA, SANTIAGO", centerX, y + 17, { align: "center" });
  doc.text("TELEFONOS: (+56 2) 2681 6656", centerX, y + 21, {
    align: "center",
  });

  // Voucher number box on the right
  const boxX = pageWidth - margin - 50;
  const boxY = y;
  const boxWidth = 50;
  const boxHeight = 22;

  doc.setDrawColor(0);
  doc.setLineWidth(0.5);
  doc.rect(boxX, boxY, boxWidth, boxHeight);

  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text("COMPROBANTE CONTABLE", boxX + boxWidth / 2, boxY + 7, {
    align: "center",
  });

  doc.setFontSize(12);
  doc.text(movement.move_number || "-", boxX + boxWidth / 2, boxY + 15, {
    align: "center",
  });

  y += 30;

  // ==================== HEADER INFO BOX ====================
  const infoBoxY = y;
  const infoBoxHeight = 28;
  doc.setLineWidth(0.3);
  doc.rect(margin, infoBoxY, pageWidth - 2 * margin, infoBoxHeight);

  // Vertical divider between the two columns
  const dividerX = pageWidth / 2 + 10;
  doc.line(dividerX, infoBoxY, dividerX, infoBoxY + infoBoxHeight);

  doc.setFontSize(7);

  // Left column — Fecha, Empresa
  let ly = infoBoxY + 7;
  const labelX = margin + 3;
  const valueX = margin + 28;

  doc.setFont("helvetica", "bold");
  doc.text("FECHA:", labelX, ly);
  doc.setFont("helvetica", "normal");
  doc.text(dayjs(movement.move_date).format("DD/MM/YYYY"), valueX, ly);

  ly += 7;
  doc.setFont("helvetica", "bold");
  doc.text("EMPRESA:", labelX, ly);
  doc.setFont("helvetica", "normal");
  doc.text(
    doc.splitTextToSize(movement.company?.name || "-", dividerX - valueX - 3),
    valueX,
    ly
  );

  // Right column — Diario, Estado
  let ry = infoBoxY + 7;
  const rLabelX = dividerX + 3;
  const rValueX = dividerX + 22;

  doc.setFont("helvetica", "bold");
  doc.text("DIARIO:", rLabelX, ry);
  doc.setFont("helvetica", "normal");
  const diarioText = movement.journal
    ? `${movement.journal.code} - ${movement.journal.name}`
    : "-";
  doc.text(
    doc.splitTextToSize(diarioText, pageWidth - margin - rValueX - 1),
    rValueX,
    ry
  );

  ry = infoBoxY + 21;
  doc.setFont("helvetica", "bold");
  doc.text("ESTADO:", rLabelX, ry);
  doc.setFont("helvetica", "normal");
  doc.text(movement.is_published ? "Publicado" : "Borrador", rValueX, ry);

  // ==================== REFERENCE ROW ====================
  y = infoBoxY + infoBoxHeight + 2;
  doc.setLineWidth(0.3);
  doc.rect(margin, y, pageWidth - 2 * margin, 8);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.text("REFERENCIA:", margin + 3, y + 5);
  doc.setFont("helvetica", "normal");
  const refText = movement.ref || "-";
  const truncatedRef =
    refText.length > 95 ? refText.substring(0, 92) + "..." : refText;
  doc.text(truncatedRef, margin + 28, y + 5);

  // ==================== LINES TABLE ====================
  y += 12;

  // Columns: Cuenta, Descripción, Débito, Crédito (content width = 190mm on A4).
  const colWidths = [55, 75, 30, 30];
  const colHeaders = ["CUENTA", "DESCRIPCIÓN", "DÉBITO", "CRÉDITO"];
  const colX: number[] = [margin];
  for (let i = 1; i < colWidths.length; i++) {
    colX.push(colX[i - 1] + colWidths[i - 1]);
  }
  // Right edges for right-aligned amount columns.
  const debitRight = colX[2] + colWidths[2] - 2;
  const creditRight = colX[3] + colWidths[3] - 2;

  const rowHeight = 7; // base height for a single-line row
  const lineStep = 3; // vertical step between wrapped text lines
  const cuentaWidth = colWidths[0] - 4;
  const descWidth = colWidths[1] - 4;

  const accountText = (line: AccountMoveLine): string =>
    line.account ? `${line.account.code} - ${line.account.name}` : "-";

  // Row height grows to fit whichever of Cuenta / Descripción wraps to more lines.
  const rowHeightFor = (line: AccountMoveLine): number => {
    const cuentaLines = doc.splitTextToSize(
      accountText(line),
      cuentaWidth
    ) as string[];
    const descLines = doc.splitTextToSize(
      line.description || "-",
      descWidth
    ) as string[];
    const maxLines = Math.max(cuentaLines.length, descLines.length);
    return Math.max(rowHeight, maxLines * lineStep + 4);
  };

  // ---- reusable table drawing helpers ----
  const drawColumnHeader = (headerY: number): number => {
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, headerY, pageWidth - 2 * margin, 6, "F");
    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.rect(margin, headerY, pageWidth - 2 * margin, 6);
    doc.setFontSize(6);
    doc.setFont("helvetica", "bold");
    doc.text(colHeaders[0], colX[0] + 2, headerY + 4);
    doc.text(colHeaders[1], colX[1] + 2, headerY + 4);
    doc.text(colHeaders[2], debitRight, headerY + 4, { align: "right" });
    doc.text(colHeaders[3], creditRight, headerY + 4, { align: "right" });
    colX.forEach((x) => {
      doc.line(x, headerY, x, headerY + 6);
    });
    doc.line(pageWidth - margin, headerY, pageWidth - margin, headerY + 6);
    return headerY + 6;
  };

  // Compact header for continuation pages.
  const drawContinuationHeader = (): number => {
    let hy = 14;
    doc.setTextColor(0);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("ALIMENTOS Y ACEITES SPA", margin, hy);
    doc.setFontSize(9);
    doc.text(
      `COMPROBANTE ${movement.move_number || ""}`,
      pageWidth - margin,
      hy,
      { align: "right" }
    );
    hy += 2;
    doc.setLineWidth(0.3);
    doc.line(margin, hy, pageWidth - margin, hy);
    hy += 4;
    doc.setFontSize(6);
    doc.setFont("helvetica", "normal");
    doc.text("(continuación)", margin, hy);
    return hy + 3;
  };

  // Outline + vertical column lines for one page's slice of the table body.
  const drawBodySegment = (segTop: number, segBottom: number): void => {
    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.rect(margin, segTop, pageWidth - 2 * margin, segBottom - segTop);
    colX.forEach((x) => {
      doc.line(x, segTop, x, segBottom);
    });
    doc.line(pageWidth - margin, segTop, pageWidth - margin, segBottom);
  };

  const drawRow = (
    line: AccountMoveLine,
    rowTop: number,
    firstOnPage: boolean
  ): void => {
    if (!firstOnPage) {
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, rowTop, pageWidth - margin, rowTop);
      doc.setDrawColor(0);
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);
    const baseY = rowTop + 5;

    const cuentaLines = doc.splitTextToSize(
      accountText(line),
      cuentaWidth
    ) as string[];
    cuentaLines.forEach((text, i) => {
      doc.text(text, colX[0] + 2, baseY + i * lineStep);
    });

    const descLines = doc.splitTextToSize(
      line.description || "-",
      descWidth
    ) as string[];
    descLines.forEach((text, i) => {
      doc.text(text, colX[1] + 2, baseY + i * lineStep);
    });

    doc.text(formatAmount(Number(line.debit)), debitRight, baseY, {
      align: "right",
    });
    doc.text(formatAmount(Number(line.credit)), creditRight, baseY, {
      align: "right",
    });
  };

  // Space reserved below the last row for the totals block + footer line.
  const totalsHeight = 12;
  const bottomReserve = totalsHeight + 20;
  const rowGuard = pageHeight - bottomReserve;

  // ---- render the table, paginating as needed ----
  let bodyTop = drawColumnHeader(y);
  let rowTop = bodyTop;
  let firstOnPage = true;

  lines.forEach((line) => {
    const rowH = rowHeightFor(line);
    if (!firstOnPage && rowTop + rowH > rowGuard) {
      drawBodySegment(bodyTop, rowTop);
      doc.addPage();
      bodyTop = drawColumnHeader(drawContinuationHeader());
      rowTop = bodyTop;
      firstOnPage = true;
    }
    drawRow(line, rowTop, firstOnPage);
    rowTop += rowH;
    firstOnPage = false;
  });

  if (lines.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text("Sin líneas registradas.", colX[0] + 2, rowTop + 5);
    doc.setTextColor(0);
    rowTop += rowHeight;
  }

  drawBodySegment(bodyTop, rowTop);

  // ==================== TOTALS ROW ====================
  const totalDebit = lines.reduce((sum, l) => sum + Number(l.debit), 0);
  const totalCredit = lines.reduce((sum, l) => sum + Number(l.credit), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  // Move the totals block to a fresh page if the final row left no room.
  if (rowTop + totalsHeight + 10 > pageHeight) {
    doc.addPage();
    drawContinuationHeader();
    rowTop = 24;
  }

  const totalsTop = rowTop;
  doc.setFillColor(240, 240, 240);
  doc.rect(margin, totalsTop, pageWidth - 2 * margin, 7, "F");
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.rect(margin, totalsTop, pageWidth - 2 * margin, 7);
  colX.forEach((x) => {
    doc.line(x, totalsTop, x, totalsTop + 7);
  });
  doc.line(pageWidth - margin, totalsTop, pageWidth - margin, totalsTop + 7);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.text("TOTAL", colX[1] + 2, totalsTop + 5);
  doc.text(formatAmount(totalDebit), debitRight, totalsTop + 5, {
    align: "right",
  });
  doc.text(formatAmount(totalCredit), creditRight, totalsTop + 5, {
    align: "right",
  });

  // Balance indicator under the totals row.
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  if (isBalanced) {
    doc.setTextColor(21, 128, 61); // green-700
    doc.text("BALANCE: CUADRADO", pageWidth - margin, totalsTop + 13, {
      align: "right",
    });
  } else {
    doc.setTextColor(185, 28, 28); // red-700
    doc.text(
      `BALANCE: DESCUADRADO — DIFERENCIA ${formatAmount(
        Math.abs(totalDebit - totalCredit)
      )}`,
      pageWidth - margin,
      totalsTop + 13,
      { align: "right" }
    );
  }
  doc.setTextColor(0);

  // ==================== FOOTER (every page) ====================
  const pageCount = doc.getNumberOfPages();
  const generatedAt = dayjs().format("DD/MM/YYYY HH:mm");
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFontSize(5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(150, 150, 150);
    const footerText =
      pageCount > 1
        ? `Documento generado el ${generatedAt}  ·  Página ${p} de ${pageCount}`
        : `Documento generado el ${generatedAt}`;
    doc.text(footerText, pageWidth / 2, pageHeight - 5, { align: "center" });
  }
  doc.setTextColor(0);

  // Auto-open the print dialog, then open the PDF in a new tab.
  doc.autoPrint();
  const pdfUrl = URL.createObjectURL(doc.output("blob"));
  window.open(pdfUrl, "_blank");
}
